import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/cart-context";
import { useLocale } from "@/context/locale-context";
import {
  useCreateOrder,
  useBoldConfig,
  createBoldOrder,
  getBoldTransactionStatus,
  cancelOrderApi,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  ShoppingBag,
  Check,
  AlertCircle,
  Loader2,
} from "lucide-react";

const BOLD_SCRIPT_SRC = "https://checkout.bold.co/library/boldPaymentButton.js";
const BOLD_BUTTON_CONTAINER_ID = "bold-payment-button-container";

/**
 * Carga (o recarga) el script del botón de pagos Bold.
 * IMPORTANTE: Bold escanea el DOM buscando <script data-bold-button="...">
 * SOLO en el instante en que su script principal se ejecuta. Por eso aquí
 * eliminamos cualquier instancia previa y lo recargamos desde cero cada
 * vez que se llama, para forzar que vuelva a escanear y detecte el nuevo
 * <script> con los datos de la venta que insertamos justo antes.
 */
function loadBoldScript() {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${BOLD_SCRIPT_SRC}"]`);
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.src = BOLD_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Botón de pagos Bold, generado dinámicamente vía un <script> con atributos
 * data-* como exige la integración manual de Bold. No usamos su versión
 * "Web Component" declarativa porque necesitamos inyectar el order-id y el
 * hash de integridad recién obtenidos del backend en el momento del click.
 */
function BoldPaymentButton({
  apiKey,
  orderId,
  amount,
  currency,
  integritySignature,
  redirectionUrl,
  description,
  customerData,
  billingAddress,
  disabled,
  onLoadError,
}) {
  const containerRef = useRef(null);
  const customerDataJson = JSON.stringify(customerData);
  const billingAddressJson = JSON.stringify(billingAddress);

  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.innerHTML = "";

    // 1) Insertamos primero el <script data-bold-button> con los datos
    //    de ESTA venta, todavía sin recargar el script principal de Bold.
    const buttonScript = document.createElement("script");
    buttonScript.setAttribute("data-bold-button", "dark-L");
    buttonScript.setAttribute("data-api-key", apiKey);
    buttonScript.setAttribute("data-order-id", orderId);
    buttonScript.setAttribute("data-currency", currency);
    buttonScript.setAttribute("data-amount", String(amount));
    buttonScript.setAttribute("data-integrity-signature", integritySignature);
    buttonScript.setAttribute("data-redirection-url", redirectionUrl);
    if (description) buttonScript.setAttribute("data-description", description.slice(0, 100));
    if (customerDataJson) buttonScript.setAttribute("data-customer-data", customerDataJson);
    if (billingAddressJson) buttonScript.setAttribute("data-billing-address", billingAddressJson);
    containerRef.current.appendChild(buttonScript);

    // 2) Recién ahora recargamos el script principal de Bold. Al ejecutarse
    //    escaneará el DOM y esta vez SÍ encontrará el script del paso 1.
    loadBoldScript().catch((error) => {
      console.error("Bold script load error:", error);
      onLoadError();
    });
  }, [
    orderId,
    integritySignature,
    apiKey,
    amount,
    currency,
    redirectionUrl,
    description,
    customerDataJson,
    billingAddressJson,
    onLoadError,
  ]);

  return (
    <div
      id={BOLD_BUTTON_CONTAINER_ID}
      ref={containerRef}
      className={disabled ? "pointer-events-none opacity-50" : ""}
      data-testid="bold-button-container"
    />
  );
}

export default function Checkout() {
  const { items, subtotalCOP, subtotalUSD, clearCart } = useCart();
  const { t, formatPrice } = useLocale();
  const createOrder = useCreateOrder();
  const { data: boldConfig } = useBoldConfig();

  const [submitted, setSubmitted] = useState(false);
  const [boldSession, setBoldSession] = useState(null); // { orderId, amount, currency, integritySignature }
  const [boldError, setBoldError] = useState(null);
  const [preparing, setPreparing] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
  });

  const boldConfigured = boldConfig?.boldConfigured;
  const boldApiKey = boldConfig?.boldApiKey; // llave de identidad (pública), NUNCA la llave secreta

  const handleBoldScriptError = useCallback(() => {
    setBoldSession(null);
    setBoldError(
      t.checkout.boldInitError || "No se pudo cargar el botón de pago de Bold. Intenta de nuevo."
    );
  }, [t.checkout.boldInitError]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const isFormValid =
    form.firstName && form.lastName && form.email && form.address && form.city && form.country;

  const buildOrderItems = () =>
    items.map((i) => ({
      paintingId: i.painting.id,
      title: i.painting.title,
      price: i.painting.onSale && i.painting.salePrice ? i.painting.salePrice : i.painting.price,
      imageUrl: i.painting.imageUrl || null,
    }));

  const saveOrder = async () => {
    return await createOrder.mutateAsync({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone || "",
      address: form.address,
      city: form.city,
      country: form.country,
      items: buildOrderItems(),
      totalUSD: subtotalUSD,
      paymentMethod: "bold",
    });
  };

  /**
   * 1. Crea la orden en NUESTRA base de datos (estado "pending_payment").
   * 2. Pide al backend el order-id + hash de integridad para Bold.
   *    El backend es la ÚNICA fuente de verdad del monto: nunca se firma
   *    un total que venga calculado en el frontend sin verificar contra
   *    los precios reales guardados en la base de datos.
   */
  const prepareBoldPayment = async () => {
    if (!isFormValid || preparing) return;
    setPreparing(true);
    setBoldError(null);
    try {
      const order = await saveOrder();
      const boldData = await createBoldOrder({ orderId: order.id });
      setBoldSession({
        internalOrderId: order.id,
        boldOrderId: boldData.boldOrderId,
        amount: boldData.amount,
        currency: boldData.currency,
        integritySignature: boldData.integritySignature,
      });
    } catch (e) {
      console.error("Bold prepare error:", e);
      setBoldError(
        e?.response?.data?.detail ||
        e?.message ||
        (t.checkout.boldInitError || "No se pudo iniciar el pago con Bold. Intenta de nuevo.")
      );
    } finally {
      setPreparing(false);
    }
  };

  if (items.length === 0 && !submitted) {
    return (
      <div className="container mx-auto px-4 py-32 text-center" data-testid="empty-checkout">
        <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mx-auto mb-6" />
        <h1 className="font-serif text-3xl mb-4">{t.cart.empty}</h1>
        <p className="text-muted-foreground mb-8">{t.cart.emptySubtitle}</p>
        <Link to="/gallery">
          <Button variant="outline" className="rounded-none uppercase tracking-widest">
            {t.cart.browseCatalog}
          </Button>
        </Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-32 text-center max-w-lg" data-testid="order-complete">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="font-serif text-4xl mb-4">{t.checkout.orderComplete}</h1>
        <p className="text-muted-foreground mb-2">{t.checkout.thankYou}</p>
        <p className="text-muted-foreground text-sm mb-10">
          {t.checkout.confirmationSent} <strong>{form.email}</strong>
        </p>
        <Link to="/gallery">
          <Button className="rounded-none uppercase tracking-widest">{t.cart.browseCatalog}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-6xl">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> {t.checkout.backToCart}
        </Link>
        <h1 className="font-serif text-3xl mb-10">{t.checkout.title}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3 space-y-10">
            {/* Buyer Info */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-semibold mb-6 pb-3 border-b">
                {t.checkout.buyerInfo}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-xs uppercase tracking-widest">
                    {t.checkout.firstName}
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="checkout-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-xs uppercase tracking-widest">
                    {t.checkout.lastName}
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="checkout-lastname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest">
                    {t.checkout.email}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="checkout-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-widest">
                    {t.checkout.phone}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="rounded-none"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="address" className="text-xs uppercase tracking-widest">
                    {t.checkout.address}
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="checkout-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-xs uppercase tracking-widest">
                    {t.checkout.city}
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="checkout-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-xs uppercase tracking-widest">
                    {t.checkout.country}
                  </Label>
                  <Input
                    id="country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    className="rounded-none"
                    data-testid="checkout-country"
                  />
                </div>
              </div>
            </section>

            {/* Payment Section — solo Bold */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-semibold mb-6 pb-3 border-b">
                {t.checkout.payment}
              </h2>

              <div className="w-full border p-5 border-foreground bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full border-2 border-foreground flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-foreground" />
                  </div>
                  <div>
                    <span className="font-bold text-sm tracking-tight">Bold</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Tarjeta, PSE, Nequi y Botón Bancolombia — pago seguro con Bold
                    </p>
                  </div>
                </div>
              </div>

              {!boldConfigured && (
                <div className="mt-6 border border-dashed bg-muted/20 p-5 space-y-2">
                  <div className="flex gap-3 text-sm text-muted-foreground">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                    <div>
                      <p className="font-medium text-foreground text-xs uppercase tracking-widest mb-2">
                        Bold no está configurado
                      </p>
                      <p className="text-xs mb-2">
                        Agrega estas variables a tu backend <code className="bg-muted px-1">.env</code>:
                      </p>
                      <div className="space-y-1.5">
                        <code className="block text-[11px] bg-muted px-3 py-1.5 font-mono">
                          BOLD_API_KEY=your_identity_key
                        </code>
                        <code className="block text-[11px] bg-muted px-3 py-1.5 font-mono">
                          BOLD_SECRET_KEY=your_secret_key
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {boldConfigured && (
                <div className="mt-6 p-6 border bg-card space-y-4">
                  {!isFormValid && (
                    <div className="flex gap-2 text-sm text-amber-600">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>{t.checkout.fillRequired}</p>
                    </div>
                  )}
                  {boldError && (
                    <div className="flex gap-2 text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>{boldError}</p>
                    </div>
                  )}

                  {!boldSession ? (
                    <Button
                      size="lg"
                      className="w-full rounded-none h-14 uppercase tracking-widest font-medium"
                      disabled={!isFormValid || preparing}
                      onClick={prepareBoldPayment}
                      data-testid="bold-prepare-btn"
                    >
                      {preparing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparando pago...
                        </>
                      ) : (
                        `Pagar ${formatPrice(subtotalUSD)} con Bold`
                      )}
                    </Button>
                  ) : (
                    <BoldPaymentButton
                      apiKey={boldApiKey}
                      orderId={boldSession.boldOrderId}
                      amount={boldSession.amount}
                      currency={boldSession.currency}
                      integritySignature={boldSession.integritySignature}
                      redirectionUrl={`${window.location.origin}/checkout/bold-return`}
                      description={`Pedido #${boldSession.internalOrderId}`}
                      customerData={{
                        email: form.email,
                        fullName: `${form.firstName} ${form.lastName}`,
                        phone: form.phone || undefined,
                      }}
                      billingAddress={{
                        address: form.address,
                        city: form.city,
                        country: form.country,
                      }}
                      onLoadError={handleBoldScriptError}
                    />
                  )}

                  <p className="text-[10px] text-center text-muted-foreground uppercase tracking-widest">
                    Serás redirigido a la pasarela segura de Bold
                  </p>
                </div>
              )}
            </section>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="border bg-card sticky top-24">
              <div className="px-6 py-5 border-b">
                <h2 className="text-xs uppercase tracking-widest font-semibold">
                  {t.checkout.orderSummary}
                </h2>
              </div>
              <div className="divide-y">
                {items.map(({ painting }) => {
                  const price =
                    painting.onSale && painting.salePrice ? painting.salePrice : painting.price;
                  return (
                    <div key={painting.id} className="flex gap-3 px-6 py-4">
                      <div className="w-14 h-16 bg-muted shrink-0 overflow-hidden">
                        {painting.imageUrl && (
                          <img
                            src={painting.imageUrl}
                            alt={painting.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-sm leading-tight truncate">{painting.title}</p>
                        {painting.categoryName && (
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                            {painting.categoryName}
                          </p>
                        )}
                        <p className="text-sm font-medium mt-1">{formatPrice(price)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-6 py-5 space-y-3 border-t">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span className="uppercase tracking-widest">{t.cart.subtotal}</span>
                  <span>{formatPrice(subtotalUSD)}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span className="uppercase tracking-widest">{t.cart.shipping}</span>
                  <span className="italic">{t.cart.shippingNote}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium">
                  <span className="uppercase tracking-widest">{t.cart.total}</span>
                  <span className="font-serif text-lg">{formatPrice(subtotalUSD)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Página de retorno tras el pago con Bold.
 * Bold redirige aquí con ?bold-order-id=...&bold-tx-status=...
 * IMPORTANTE: bold-tx-status es solo para UX inmediata. El estado real y
 * definitivo de la orden en nuestra base de datos SOLO debe actualizarse
 * mediante el webhook de Bold en el backend (ver bold_backend.py),
 * nunca confiando en el query param que puede ser manipulado por el usuario.
 */
export function CheckoutBoldReturn() {
  const [params] = useSearchParams();
  const { clearCart } = useCart();
  const boldOrderId = params.get("bold-order-id");
  const txStatus = params.get("bold-tx-status"); // approved | rejected | pending | failed (solo informativo)

  const [status, setStatus] = useState("checking"); // checking | approved | pending | rejected | error
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    if (!boldOrderId) {
      setStatus("error");
      return;
    }
    getBoldTransactionStatus(boldOrderId)
      .then((data) => {
        setOrderInfo(data);
        const s = (data.paymentStatus || "").toUpperCase();
        if (s === "APPROVED") setStatus("approved");
        else if (s === "REJECTED" || s === "FAILED" || s === "VOIDED") setStatus("rejected");
        else setStatus("pending"); // PROCESSING, PENDING, NO_TRANSACTION_FOUND
      })
      .catch(() => setStatus(txStatus === "approved" ? "pending" : "error"));
  }, [boldOrderId, txStatus]);

  useEffect(() => {
    if (status === "approved") clearCart();
  }, [status, clearCart]);

  return (
    <div className="container mx-auto px-4 py-32 text-center max-w-lg">
      {status === "checking" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto mb-6 animate-spin text-muted-foreground" />
          <h1 className="font-serif text-3xl mb-4">Verificando tu pago...</h1>
        </>
      )}

      {status === "approved" && (
        <>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-serif text-4xl mb-4">Pago confirmado</h1>
          <p className="text-muted-foreground mb-2">Tu pedido ha sido recibido y pagado con éxito.</p>
        </>
      )}

      {status === "pending" && (
        <>
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="font-serif text-3xl mb-4">Pago en proceso</h1>
          <p className="text-muted-foreground mb-2">
            Estamos confirmando tu transacción. Te notificaremos por correo cuando se apruebe.
          </p>
        </>
      )}

      {(status === "rejected" || status === "error") && (
        <>
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-serif text-3xl mb-4">
            {status === "rejected" ? "Pago rechazado" : "No pudimos verificar tu pago"}
          </h1>
          <p className="text-muted-foreground mb-8">
            {status === "rejected"
              ? "La transacción no fue aprobada. No se realizó ningún cargo."
              : "Contáctanos si crees que esto es un error."}
          </p>
        </>
      )}

      {boldOrderId && (
        <p className="text-muted-foreground text-sm mb-10">
          Referencia <strong>#{boldOrderId}</strong>
        </p>
      )}

      <Link to="/gallery">
        <Button className="rounded-none uppercase tracking-widest">Volver a la galería</Button>
      </Link>
    </div>
  );
}

/**
 * Ruta de abandono/cancelación (origin-url). Bold redirige aquí si el
 * usuario cierra el checkout sin completar el pago.
 */
export function CheckoutBoldCancel() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");

  useEffect(() => {
    if (orderId) {
      cancelOrderApi(orderId).catch(() => {});
    }
  }, [orderId]);

  return (
    <div className="container mx-auto px-4 py-32 text-center max-w-lg">
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
        <AlertCircle className="w-8 h-8 text-red-500" />
      </div>
      <h1 className="font-serif text-3xl mb-4">Pago cancelado</h1>
      <p className="text-muted-foreground mb-8">Tu pedido no fue procesado. No se realizó ningún cargo.</p>
      <Link to="/gallery">
        <Button variant="outline" className="rounded-none uppercase tracking-widest">
          Volver a la galería
        </Button>
      </Link>
    </div>
  );
}